import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, Paper, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SpeedIcon from '@mui/icons-material/Speed';
import MapIcon from '@mui/icons-material/Map';
import SatelliteIcon from '@mui/icons-material/Satellite';
import { deltaToColor } from './sectorColors';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILES = {
  satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles &copy; Esri' },
  osm:       { url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap contributors' },
};
const LAP_COLORS = ['#FF9800', '#F06292', '#AED581', '#4DD0E1', '#FFD54F', '#A5D6A7', '#9FA8DA'];

// ── FitBounds ────────────────────────────────────────────────────
function FitBounds({ trackPoints }) {
  const map = useMap();
  useEffect(() => {
    if (!trackPoints?.length) return;
    const lats = trackPoints.map(p => p.lat);
    const lons = trackPoints.map(p => p.lon);
    const bounds = [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]];
    // Piccolo delay per assicurarsi che il container abbia la sua altezza finale
    const t = setTimeout(() => {
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [24, 24] });
    }, 80);
    return () => clearTimeout(t);
  }, [trackPoints, map]);
  return null;
}

// ── Tutti i layer della pista in un solo useEffect ────────────────
function TrackLayers({ trackPoints, refPoints, sectorPaths, sectors, activeSector, onSectorClick, showBestLap, showRef, otherLaps, activeLapNums }) {
  const map = useMap();
  const layersRef = useRef([]);

  useEffect(() => {
    // Rimuovi tutti i layer precedenti
    layersRef.current.forEach(l => { try { map.removeLayer(l); } catch(_) {} });
    layersRef.current = [];
    const add = (l) => { l.addTo(map); layersRef.current.push(l); return l; };

    // ── Tracciato base ────────────────────────────────────────────
    if (showBestLap && trackPoints.length > 5)
      add(L.polyline(trackPoints.map(p => [p.lat, p.lon]),
        { color: 'rgba(255,255,255,0.2)', weight: 5, interactive: false }));

    // ── Riferimento ───────────────────────────────────────────────
    if (showRef && refPoints.length > 5)
      add(L.polyline(refPoints.map(p => [p.lat, p.lon]),
        { color: 'rgba(80,80,200,0.75)', weight: 3, dashArray: '8 4', interactive: false }));

    // ── Settori colorati ──────────────────────────────────────────
    sectorPaths.forEach(s => {
      const active = s.sector === activeSector;
      const line = add(L.polyline(s.positions,
        { color: s.color, weight: active ? 5 : 3, opacity: active ? 1 : 0.85 }));
      if (onSectorClick) line.on('click', () => onSectorClick(s.sector));
    });

    // ── Label settori ─────────────────────────────────────────────
    sectors.filter(s => s.lat_mid && s.lon_mid).forEach(s => {
      const delta  = s.delta_vs_ref_s ?? s.delta_vs_best_s ?? 0;
      const color  = deltaToColor(Math.max(0, delta));
      const active = s.sector === activeSector;
      const r = active ? 13 : 10;
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:${r*2}px;height:${r*2}px;border-radius:50%;background:${color};border:2px solid rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;font-size:${r<11?9:10}px;font-weight:800;color:#0A0A0A;font-family:Inter,sans-serif;transform:translate(-50%,-50%);cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,0.5)${active?`,0 0 10px ${color}`:''}">${s.sector}</div>`,
        iconSize: [r*2, r*2], iconAnchor: [r, r],
      });
      const m = add(L.marker([s.lat_mid, s.lon_mid], { icon, zIndexOffset: 500 }));
      if (onSectorClick) m.on('click', () => onSectorClick(s.sector));
    });

    // ── Traiettorie altri giri ────────────────────────────────────
    (otherLaps || []).forEach((lap, idx) => {
      if (!activeLapNums.has(lap.lap_number)) return;
      if (!lap.gps_trace?.length) return;
      add(L.polyline(lap.gps_trace,
        { color: LAP_COLORS[idx % LAP_COLORS.length], weight: 3, opacity: 0.9, interactive: false }));
    });

    return () => {
      layersRef.current.forEach(l => { try { map.removeLayer(l); } catch(_) {} });
      layersRef.current = [];
    };
  }, [trackPoints, refPoints, sectorPaths, sectors, activeSector,
      showBestLap, showRef, otherLaps, activeLapNums, map, onSectorClick]);

  return null;
}

// ── Pallino singolo ───────────────────────────────────────────────
function DotMarker({ lat, lon, color, size, glowColor, zOffset = 1000 }) {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) { try { map.removeLayer(ref.current); } catch(_) {} ref.current = null; }
    if (!lat || !lon) return;
    const icon = L.divIcon({
      className: '',
      html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2.5px solid rgba(0,0,0,0.35);transform:translate(-50%,-50%);box-shadow:0 0 8px ${glowColor||color};pointer-events:none;"></div>`,
      iconSize: [size, size], iconAnchor: [size/2, size/2],
    });
    ref.current = L.marker([lat, lon], { icon, interactive: false, zIndexOffset: zOffset }).addTo(map);
    return () => { if (ref.current) { try { map.removeLayer(ref.current); } catch(_) {} ref.current = null; } };
  }, [lat, lon, color, size, glowColor, zOffset, map]);
  return null;
}

// ── Componente principale ─────────────────────────────────────────
export default function TrackMapLeaflet({
  trackData, activeSector, onSectorClick,
  hoveredPoint = null, pinnedPoint = null,
  telemetry = null, activeLapNums = new Set(),
  compact = false,   // modalità compatta: no header sessione, altezza ridotta
}) {
  const { t } = useTranslation('race_engineer');
  const [tileMode, setTileMode]       = useState('satellite');
  const [showBestLap, setShowBestLap] = useState(true);
  const [showRef, setShowRef]         = useState(true);

  const trackPoints = trackData?.track_gps     ?? [];
  const refPoints   = trackData?.reference_gps ?? [];
  const sectors     = trackData?.sectors       ?? [];

  const center = useMemo(() => {
    if (!trackPoints.length) return [45.07, 9.10];
    const lats = trackPoints.map(p => p.lat);
    const lons = trackPoints.map(p => p.lon);
    return [(Math.min(...lats)+Math.max(...lats))/2, (Math.min(...lons)+Math.max(...lons))/2];
  }, [trackPoints]);

  // Altezza dinamica basata sull'aspect ratio della pista
  const mapHeight = useMemo(() => {
    if (!trackPoints.length) return 450;
    const lats = trackPoints.map(p => p.lat);
    const lons = trackPoints.map(p => p.lon);
    const latRange = (Math.max(...lats) - Math.min(...lats)) || 1;
    const lonRange = ((Math.max(...lons) - Math.min(...lons)) *
      Math.cos(Math.PI / 180 * ((Math.min(...lats)+Math.max(...lats))/2))) || 1;
    const ratio = lonRange / latRange;
    // ~900px width → height = 900/ratio, clampato tra 350 e 650px
    return Math.min(Math.max(Math.round(900 / ratio), 350), 650);
  }, [trackPoints]);

  const sectorPaths = useMemo(() => sectors.map(s => {
    if (s.t_start == null || s.t_end == null) return null;
    const pts = trackPoints.filter(p => p.t >= s.t_start && p.t < s.t_end);
    if (pts.length < 2) return null;
    const delta = s.delta_vs_ref_s ?? s.delta_vs_best_s ?? 0;
    return { ...s, positions: pts.map(p => [p.lat, p.lon]), color: deltaToColor(Math.max(0, delta)) };
  }).filter(Boolean), [trackPoints, sectors]);

  const otherLaps = telemetry?.other_laps;

  if (!trackData) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ p: 2, bgcolor: 'rgba(63,81,181,0.08)', borderRadius: 3, display: 'flex' }}>
          <SpeedIcon sx={{ fontSize: 40, color: 'secondary.main', opacity: 0.6 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">{t('map.empty_title')}</Typography>
        <Typography color="text.disabled" variant="body2">{t('map.empty_message')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>

      {/* Header — full o compact */}
      {compact ? (
        /* Compact: solo toggle satellite/mappa */
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', bgcolor: 'background.paper' }}>
          <ToggleButtonGroup size="small" exclusive value={tileMode} onChange={(_, v) => v && setTileMode(v)}
            sx={{ '& .MuiToggleButton-root': { py: 0.15, px: 0.5 } }}>
            <ToggleButton value="satellite"><SatelliteIcon sx={{ fontSize: 14 }} /></ToggleButton>
            <ToggleButton value="osm"><MapIcon sx={{ fontSize: 14 }} /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : (
        /* Full header */
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'background.paper', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{trackData.session ?? 'Sessione'}</Typography>
            <Typography variant="caption" color="text.secondary">
              Best: {trackData.best_lap_time} · Teorico: {trackData.best_theoretical}
            </Typography>
            {trackData.reference_racer && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Box sx={{ width: 20, height: 3, bgcolor: 'rgba(80,80,200,0.6)', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Rif. · {trackData.reference_racer}: <strong>{trackData.reference_lap_time}</strong>
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {[{ label: t('map.legend_perfect'), color: 'rgb(0,200,30)' }, { label: t('map.legend_margin'), color: 'rgb(255,180,30)' }, { label: t('map.legend_critical'), color: 'rgb(255,30,30)' }].map(({ label, color }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Box>
            ))}
            <Chip label={t('map.best')} size="small" onClick={() => setShowBestLap(v => !v)}
              sx={{ fontSize: 10, height: 20, cursor: 'pointer', border: '1px solid',
                bgcolor:     showBestLap ? 'rgba(255,255,255,0.12)' : 'transparent',
                borderColor: showBestLap ? 'rgba(255,255,255,0.4)'  : 'action.disabled',
                color:       showBestLap ? 'text.primary' : 'text.disabled',
              }} />
            {refPoints.length > 5 && (
              <Chip label={t('map.ref')} size="small" onClick={() => setShowRef(v => !v)}
                sx={{ fontSize: 10, height: 20, cursor: 'pointer', border: '1px solid',
                  bgcolor:     showRef ? 'rgba(80,80,200,0.2)'   : 'transparent',
                  borderColor: showRef ? 'rgba(100,100,220,0.6)' : 'action.disabled',
                  color:       showRef ? 'rgba(150,150,255,1)' : 'text.disabled',
                }} />
            )}
            <ToggleButtonGroup size="small" exclusive value={tileMode} onChange={(_, v) => v && setTileMode(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 0.75 } }}>
              <ToggleButton value="satellite"><SatelliteIcon sx={{ fontSize: 16 }} /></ToggleButton>
              <ToggleButton value="osm"><MapIcon sx={{ fontSize: 16 }} /></ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {/* Mappa — altezza adattiva (ridotta in compact) */}
      <Box sx={{ height: compact ? Math.min(mapHeight, 320) : mapHeight, '& .leaflet-container': { height: '100%', width: '100%', background: '#1a1a1a' } }}>
        <MapContainer center={center} zoom={14} style={{ height: '100%', width: '100%' }}
          zoomControl scrollWheelZoom>
          <TileLayer url={TILES[tileMode].url} attribution={TILES[tileMode].attribution}
            maxNativeZoom={19} maxZoom={22} key={tileMode} />

          <FitBounds trackPoints={trackPoints} />

          {/* Tutti i layer vettoriali in un unico componente imperativo */}
          <TrackLayers
            trackPoints={trackPoints}
            refPoints={refPoints}
            sectorPaths={sectorPaths}
            sectors={sectors}
            activeSector={activeSector}
            onSectorClick={onSectorClick}
            showBestLap={showBestLap}
            showRef={showRef}
            otherLaps={otherLaps}
            activeLapNums={activeLapNums}
          />

          {/* Pallino hover (solo se non c'è pin) */}
          <DotMarker
            lat={hoveredPoint?.lat && !pinnedPoint ? hoveredPoint.lat : null}
            lon={hoveredPoint?.lon && !pinnedPoint ? hoveredPoint.lon : null}
            color="white" size={14} glowColor="rgba(255,255,255,0.8)"
          />

          {/* Pallino pin */}
          <DotMarker
            lat={pinnedPoint?.lat ?? null}
            lon={pinnedPoint?.lon ?? null}
            color="#FFEB3B" size={18} glowColor="rgba(255,235,59,0.9)"
            zOffset={1100}
          />
        </MapContainer>
      </Box>
    </Paper>
  );
}
